import React from "react";
import { isEmpty } from "lodash";
import { InvalidQueryConfig } from "../../../generated/graphql";
import { spacing } from "../../../stitches.config";
import { BANNER_TYPES } from "../__styles__/Banner";
import { FlexRow } from "../__styles__/Layout";
import { Emphasis } from "../__styles__/Typography";
import Banner from "../Banner";
import { stripCustomMapFieldPrefix } from "./utils";

export interface InvalidQueryConfigBannerProps {
  invalidQueryConfig?: InvalidQueryConfig | null;
}

export const InvalidQueryConfigBanner = ({
  invalidQueryConfig,
}: InvalidQueryConfigBannerProps) => {
  if (!invalidQueryConfig) {
    return null;
  }

  const { fields, filters, orderByReset } = invalidQueryConfig;
  const hasInvalidFields = !isEmpty(fields);
  const hasInvalidFilters = !isEmpty(filters);

  if (!hasInvalidFields && !hasInvalidFilters && !orderByReset) {
    return null;
  }

  const listItems: React.ReactNode[] = [];

  if (hasInvalidFields) {
    const fieldNames = fields.map(f => stripCustomMapFieldPrefix(f.name));
    const fieldCount = fields.length;
    listItems.push(
      <li key="fields">
        {fieldCount} column{fieldCount > 1 ? "s" : ""} ({fieldNames.join(", ")})
        {" — "}
        <span style={{ fontStyle: "italic" }}>
          highlighted in red in the table header
        </span>
      </li>,
    );
  }

  if (hasInvalidFilters) {
    const filterNames = filters.map(f => stripCustomMapFieldPrefix(f.field));
    const filterCount = filters.length;
    listItems.push(
      <li key="filters">
        {filterCount} filter{filterCount > 1 ? "s" : ""} (
        {filterNames.join(", ")}){" — "}
        <span style={{ fontStyle: "italic" }}>
          highlighted in red in the filter dropdown
        </span>
      </li>,
    );
  }

  if (orderByReset) {
    listItems.push(<li key="orderBy">Sort order was reset to default</li>);
  }

  return (
    <FlexRow
      style={{
        padding: `0px ${spacing.l} ${spacing.m} ${spacing.l}`,
      }}
    >
      <Banner
        items={[
          <div key="invalid-query-config-message">
            <Emphasis>
              This saved view references data that no longer exists.
            </Emphasis>{" "}
            Remove the affected items and save this view to dismiss this
            warning.
            <ul
              style={{
                margin: "4px 0 0 0",
                paddingLeft: "24px",
              }}
            >
              {listItems}
            </ul>
          </div>,
        ]}
        type={BANNER_TYPES.ATTENTION}
      />
    </FlexRow>
  );
};
